import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import {
    ConfirmationWindowComponent,
    ConfirmationWindowTypes,
} from '@components/confirmation-window/confirmation-window.component';
import { ChannelCategory } from 'icreate/common/models/channel-category';
import { fromEvent, lastValueFrom, Subject, take, takeUntil } from 'rxjs';

export enum InputValueState {
    error,
    normal,
    init,
}

export enum SystemSettingInputSemantic {
    secondary = 'secondary',
    tertiary = 'tertiary',
}

export enum SystemSettingInputWeight {
    regular = 'regular',
    bold = 'bold',
}

@Component({
    selector: 'app-system-setting-dropdown',
    templateUrl: './system-setting-dropdown.component.html',
    styleUrls: ['./system-setting-dropdown.component.css'],
    standalone: false,
})
export class SystemSettingDropdownComponent implements OnInit, OnChanges, OnDestroy {
    @ViewChild('container') container: ElementRef;
    @ViewChild('confirmationDialog') confirmationDialog: ConfirmationWindowComponent;

    readonly MAX_HEIGHT = 216;
    readonly ROW_HEIGHT = 48;

    readonly SECONDARY = SystemSettingInputSemantic.secondary;
    readonly TERTIARY = SystemSettingInputSemantic.tertiary;

    readonly REGULAR = SystemSettingInputWeight.regular;
    readonly BOLD = SystemSettingInputWeight.bold;

    readonly ERROR = InputValueState.error;
    readonly NORMAL = InputValueState.normal;
    readonly INIT = InputValueState.init;

    @Input() name: string;
    @Input() displayEdit: boolean = false;
    @Input() label: string;
    @Input() itemList: any[];
    @Input() value: string | number = '';
    @Input() dark: boolean = false;
    @Input() height: number = 0;
    @Input() semantic: SystemSettingInputSemantic = this.SECONDARY;
    @Input() inputErrorMessage: string = '';
    @Input() emptyItem: string = '';
    @Input() id: string | null = null;
    @Input() isDropdownBelow: boolean = false;
    @Input() changedBeforeShowDialog: boolean = false;
    @Input() mainMessage: string = '';
    @Input() subMessage: string = '';
    @Input() dropdownWidth: string = 'fit-content';
    @Input() disabled: boolean = false;
    @Input() selectedValues: string;
    @Output() onInputChange = new EventEmitter();

    inputValue: string | number = '';
    isShowSelect = false;
    inputValueCheck = this.INIT;

    optionsTop: string;
    optionsLeft: string;

    private destroy$: Subject<void> = new Subject();

    @HostListener('document:click', ['$event']) clickOut(event: MouseEvent) {
        if (!this.container?.nativeElement.contains(event.target) && this.isShowSelect) {
            this.isShowSelect = false;
        }
    }

    ngOnInit() {
        this.inputValue = this.value || this.value == 0 ? this.value : '';

        const scrollContainer = document.querySelector('.system-setting-scroll-parent');
        if (scrollContainer) {
            fromEvent(scrollContainer, 'scroll')
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                    this.isShowSelect = false;
                });
        }

        const scrollContainer2 = document.querySelector('.scroll-content');
        if (scrollContainer2) {
            fromEvent(scrollContainer2, 'scroll')
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                    this.isShowSelect = false;
                });
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.itemList || changes.value) {
            this.inputValue = this.value;
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onCheckInputValue() {
        if (this.inputErrorMessage) {
            this.inputValueCheck = this.ERROR;
        } else {
            this.inputValueCheck = this.NORMAL;
        }
    }

    async selectValue(event: MouseEvent, value: ChannelCategory | string, id: string | null) {
        if (this.disabled) return;
        event.preventDefault();
        if (typeof value !== 'string' && typeof value !== 'number') {
            this.onInputChange.emit({ name: this.name, value: value.id });
        } else {
            if (this.changedBeforeShowDialog) {
                if (value !== this.inputValue) {
                    try {
                        this.confirmationDialog.setVisible(true);
                        const option = await lastValueFrom(this.confirmationDialog.onOptionSelected.pipe(take(1)));
                        if (option === ConfirmationWindowTypes.SAVE) {
                            this.onInputChange.emit({ name: this.name, value: value, id: id });
                        }
                    } catch (error) {
                        console.log(error);
                    }
                } else {
                    this.onInputChange.emit({ name: this.name, value: value, id: id });
                }
            } else {
                this.onInputChange.emit({ name: this.name, value: value, id: id });
            }
        }
        this.isShowSelect = false;
        this.onCheckInputValue();
    }

    openSelect() {
        if (this.disabled) return;
        this.isShowSelect = !this.isShowSelect;
        if (this.isShowSelect) {
            this.updatePosition();
            this.updateWidth();
        }
        this.onCheckInputValue();
    }

    private updatePosition() {
        if (!this.container) return;
        const containerPosition = this.container.nativeElement.getBoundingClientRect();
        const scrollTop = window.scrollY;
        const scrollLeft = window.scrollX;

        const top = containerPosition.top + scrollTop;

        if (this.isDropdownBelow) {
            this.optionsTop = containerPosition.bottom + scrollTop + 2 + 'px';
            this.optionsLeft = 'unset';
            return;
        }

        this.optionsTop = top + 'px';
        this.optionsLeft = containerPosition.right + scrollLeft + 'px';

        if (window.innerHeight < top + this.MAX_HEIGHT) {
            this.optionsTop = top - this.MAX_HEIGHT + this.ROW_HEIGHT + 'px';
        }
    }

    private updateWidth() {
        if (!this.isDropdownBelow || !this.container) return;
        const containerPosition = this.container.nativeElement.getBoundingClientRect();
        if (containerPosition && containerPosition.width) {
            this.dropdownWidth = containerPosition.width + 'px';
        }
    }

    displayEditButton(item) {
        return this.emptyItem !== this.inputValue && item === this.inputValue && this.displayEdit;
    }
}
